import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, LogOut, User, Upload, PenTool, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

export default function HeaderWithAuth() {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };
  
  const handleLogout = async () => {
    await logout();
    navigate('/');
  };
  
  // Get user initials for the avatar
  const getUserInitials = () => {
    if (!user?.email) return 'U';
    
    const email = user.email;
    if (email.includes('@')) {
      const name = email.split('@')[0];
      return name.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // Check if link is active
  const isActive = (path: string) => location.pathname === path;
  
  return (
    <header className="border-b border-border bg-background">
      <div className="container flex h-16 items-center justify-between">
        <div className="hidden md:flex items-center space-x-1">
          <Button 
            variant={isActive('/dashboard') ? "default" : "ghost"} 
            asChild 
            className="hover:bg-accent hover:text-accent-foreground"
          >
            <Link to="/dashboard">Dashboard</Link>
          </Button>
          <Button 
            variant={isActive('/videos') ? "default" : "ghost"} 
            asChild 
            className="hover:bg-accent hover:text-accent-foreground"
          >
            <Link to="/videos">Your Videos</Link>
          </Button>
          <Button 
            variant={isActive('/upload') ? "default" : "ghost"} 
            asChild 
            className="hover:bg-accent hover:text-accent-foreground"
          >
            <Link to="/upload">Upload</Link>
          </Button>
          <Button 
            variant={isActive('/search') ? "default" : "ghost"} 
            asChild 
            className="hover:bg-accent hover:text-accent-foreground"
          >
            <Link to="/search">Search</Link>
          </Button>
          <Button 
            variant={isActive('/playground') ? "default" : "ghost"} 
            asChild 
            className="hover:bg-accent hover:text-accent-foreground"
          >
            <Link to="/playground">Playground</Link>
          </Button>
        </div>

        <div className="flex-1 max-w-md mx-auto px-4">
          <form onSubmit={handleSearch}>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search across videos..."
                className="w-full pl-8 bg-background transition-all duration-300 focus-within:shadow-md"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </form>
        </div>

        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            asChild 
            className="md:hidden hover:bg-accent hover:text-accent-foreground transition-all duration-200"
          >
            <Link to="/videos">
              <Video className="h-5 w-5" />
            </Link>
          </Button>
          <Button 
            variant="ghost" 
            asChild 
            className="md:hidden hover:bg-accent hover:text-accent-foreground transition-all duration-200"
          >
            <Link to="/upload">
              <Upload className="h-5 w-5" />
            </Link>
          </Button>
          <Button 
            variant="ghost" 
            asChild 
            className="md:hidden hover:bg-accent hover:text-accent-foreground transition-all duration-200"
          >
            <Link to="/playground">
              <PenTool className="h-5 w-5" />
            </Link>
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className="h-8 w-8 rounded-full bg-video-blue text-white flex items-center justify-center cursor-pointer hover:scale-110 transition-transform duration-200">
                {getUserInitials()}
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/profile" className="cursor-pointer flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>Profile</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} className="cursor-pointer flex items-center gap-2">
                <LogOut className="h-4 w-4" />
                <span>Logout</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
